// src/stores/groupChatStore.js
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  collection,
  doc,
  setDoc,
  addDoc,
  serverTimestamp,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  updateDoc,
  writeBatch,
  arrayUnion,
  arrayRemove,
  deleteDoc,
  increment,
} from 'firebase/firestore'
import { onSnapshot } from '@/services/sessionListeners'
import { db } from '@/services/firebaseconfig'
import { playGroupChatNotificationTone } from '@/utils/audioTones'

export const useGroupChatStore = defineStore('groupChat', () => {
  // ═══════════════════════════════════════════════════════════════════
  // State
  // ═══════════════════════════════════════════════════════════════════

  const myUid = ref(null)
  const myName = ref('')

  // Track if user is actively viewing groups tab (to suppress notification sounds)
  const isGroupsTabActive = ref(false)

  // All groups the user is a member of
  const groups = ref([]) // Array of { groupId, name, members, lastMessage, lastMessageAt, unreadCount, mutedUntil }
  const groupsUnsubscribe = ref(null)

  // Currently active group
  const activeGroupId = ref(null)
  const activeGroup = ref(null) // { groupId, name, members, createdBy, createdAt }
  const messages = ref([]) // Messages in active group
  const messagesUnsubscribe = ref(null)

  // Typing indicators
  const typingUsers = ref({}) // { groupId: { uid: { name, timestamp } } }
  const typingUnsubscribe = ref(null)

  // Search and sort state
  const searchQuery = ref('')
  const sortBy = ref('recent') // 'recent' | 'created' | 'name'

  // Member details cache (for displaying names/avatars)
  const memberDetailsCache = ref({}) // { uid: { displayName, email, photoURL } }

  // Total unread count across all groups
  const totalUnreadCount = computed(() => {
    return groups.value.reduce((sum, g) => {
      // Don't count muted groups
      if (g.mutedUntil && new Date(g.mutedUntil.toDate?.() || g.mutedUntil) > new Date()) {
        return sum
      }
      return sum + (g.unreadCount || 0)
    }, 0)
  })

  // Filtered and sorted groups
  const filteredGroups = computed(() => {
    let result = [...groups.value]

    // Apply search filter
    if (searchQuery.value.trim()) {
      const q = searchQuery.value.toLowerCase().trim()
      result = result.filter(g => {
        // Search by group name
        if (g.name?.toLowerCase().includes(q)) return true
        // Search by member names
        const memberNames = g.memberNames || []
        return memberNames.some(name => name?.toLowerCase().includes(q))
      })
    }

    // Apply sorting
    if (sortBy.value === 'recent') {
      result.sort((a, b) => {
        const aTime = a.lastMessageAt?.toDate?.() || a.lastMessageAt || 0
        const bTime = b.lastMessageAt?.toDate?.() || b.lastMessageAt || 0
        return new Date(bTime) - new Date(aTime)
      })
    } else if (sortBy.value === 'created') {
      result.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || a.createdAt || 0
        const bTime = b.createdAt?.toDate?.() || b.createdAt || 0
        return new Date(bTime) - new Date(aTime)
      })
    } else if (sortBy.value === 'name') {
      result.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    }

    return result
  })

  // Check if current user is typing in active group
  const isRecipientTyping = computed(() => {
    if (!activeGroupId.value) return false
    const groupTyping = typingUsers.value[activeGroupId.value] || {}
    // Check if anyone other than me is typing
    return Object.entries(groupTyping).some(([uid, data]) => {
      if (uid === myUid.value) return false
      const timestamp = data.timestamp?.toDate?.() || data.timestamp
      return timestamp && Date.now() - new Date(timestamp).getTime() < 3000
    })
  })

  // Get names of people currently typing
  const typingNames = computed(() => {
    if (!activeGroupId.value) return []
    const groupTyping = typingUsers.value[activeGroupId.value] || {}
    return Object.entries(groupTyping)
      .filter(([uid, data]) => {
        if (uid === myUid.value) return false
        const timestamp = data.timestamp?.toDate?.() || data.timestamp
        return timestamp && Date.now() - new Date(timestamp).getTime() < 3000
      })
      .map(([, data]) => data.name || 'Someone')
  })

  // ═══════════════════════════════════════════════════════════════════
  // Initialization
  // ═══════════════════════════════════════════════════════════════════

  async function initialize(uid, displayName) {
    myUid.value = uid
    myName.value = displayName

    // Start listening to groups
    startGroupsListener()

    console.log('[GroupChatStore] Initialized for:', uid)
  }

  function cleanup() {
    stopGroupsListener()
    stopMessagesListener()
    stopTypingListener()
    groups.value = []
    activeGroupId.value = null
    activeGroup.value = null
    messages.value = []
    // Back to the state before initialize(): App.vue re-initialises only when myUid is empty
    // (docs/bugs/walkie_call_lifecycle_BUG.md).
    myUid.value = null
    myName.value = ''
  }

  // ═══════════════════════════════════════════════════════════════════
  // Groups Listener
  // ═══════════════════════════════════════════════════════════════════

  let isFirstLoad = true
  const memberStatusUnsubscribes = []

  function startGroupsListener() {
    if (!myUid.value) return

    // Query all groups where user is a member
    const groupsRef = collection(db, 'groupChats')
    const q = query(groupsRef, where('members', 'array-contains', myUid.value))

    groupsUnsubscribe.value = onSnapshot(
      q,
      async snapshot => {
        // Clean up old memberStatus listeners
        memberStatusUnsubscribes.forEach(unsub => unsub())
        memberStatusUnsubscribes.length = 0

        const newGroups = []

        for (const docSnap of snapshot.docs) {
          const data = docSnap.data()

          // Get member names for search
          const memberNames = await getMemberNames(data.members || [])

          newGroups.push({
            groupId: docSnap.id,
            ...data,
            memberNames,
            unreadCount: 0,
            mutedUntil: null,
          })

          // Set up real-time listener for this group's memberStatus
          const statusRef = doc(db, 'groupChats', docSnap.id, 'memberStatus', myUid.value)
          const unsub = onSnapshot(
            statusRef,
            statusSnap => {
              const groupIndex = groups.value.findIndex(g => g.groupId === docSnap.id)
              if (groupIndex === -1) return

              const oldUnread = groups.value[groupIndex].unreadCount || 0
              const newUnread = statusSnap.exists() ? statusSnap.data().unreadCount || 0 : 0
              const mutedUntil = statusSnap.exists() ? statusSnap.data().mutedUntil : null

              // Update the group's unread count
              groups.value[groupIndex].unreadCount = newUnread
              groups.value[groupIndex].mutedUntil = mutedUntil

              // Check if we should play a sound
              const isMuted =
                mutedUntil && new Date(mutedUntil.toDate?.() || mutedUntil) > new Date()

              if (!isFirstLoad && newUnread > oldUnread && !isGroupsTabActive.value && !isMuted) {
                console.log(
                  '[GroupChatStore] Playing group notification sound (memberStatus listener)'
                )
                playGroupChatNotificationTone()
              }
            },
            _error => {
              // Ignore errors on memberStatus - may not exist yet
            }
          )

          memberStatusUnsubscribes.push(unsub)
        }

        isFirstLoad = false
        groups.value = newGroups
      },
      error => {
        if (error.code === 'permission-denied') {
          console.log('[GroupChatStore] No groups yet (collection may not exist)')
          groups.value = []
        } else {
          console.error('[GroupChatStore] Groups listener error:', error)
        }
      }
    )
  }

  function stopGroupsListener() {
    if (groupsUnsubscribe.value) {
      groupsUnsubscribe.value()
      groupsUnsubscribe.value = null
    }
    // Clean up memberStatus listeners
    memberStatusUnsubscribes.forEach(unsub => unsub())
    memberStatusUnsubscribes.length = 0
  }

  // ═══════════════════════════════════════════════════════════════════
  // Member Names Helper
  // ═══════════════════════════════════════════════════════════════════

  async function getMemberNames(memberUids) {
    const names = []
    for (const uid of memberUids) {
      if (memberDetailsCache.value[uid]) {
        const cached = memberDetailsCache.value[uid]
        names.push(cached.identity?.displayName || cached.displayName || 'Unknown')
      } else {
        try {
          // Try users collection first
          const userDoc = await getDoc(doc(db, 'users', uid))
          if (userDoc.exists()) {
            const userData = userDoc.data()
            memberDetailsCache.value[uid] = userData
            names.push(
              userData.identity?.displayName || userData.displayName || userData.email || 'Unknown'
            )
          } else {
            names.push('Unknown')
          }
        } catch (e) {
          // Fallback to publicUsers collection
          try {
            const publicUserDoc = await getDoc(doc(db, 'publicUsers', uid))
            if (publicUserDoc.exists()) {
              const userData = publicUserDoc.data()
              memberDetailsCache.value[uid] = userData
              names.push(
                userData.identity?.displayName ||
                  userData.displayName ||
                  userData.email ||
                  'Unknown'
              )
            } else {
              names.push('Unknown')
            }
          } catch (e2) {
            names.push('Unknown')
          }
        }
      }
    }
    return names
  }

  function normalizeMember(uid, data) {
    return {
      uid,
      displayName:
        data.identity?.displayName ||
        data.displayName ||
        data.identity?.email ||
        data.email ||
        'Unknown User',
      email: data.identity?.email || data.email || '',
    }
  }

  async function getMemberDetails(memberUids) {
    const details = []
    for (const uid of memberUids) {
      if (memberDetailsCache.value[uid]) {
        details.push(normalizeMember(uid, memberDetailsCache.value[uid]))
      } else {
        try {
          // Try users collection first
          const userDoc = await getDoc(doc(db, 'users', uid))
          if (userDoc.exists()) {
            const userData = userDoc.data()
            memberDetailsCache.value[uid] = userData
            details.push(normalizeMember(uid, userData))
          } else {
            details.push({ uid, displayName: 'Unknown User', email: '' })
          }
        } catch (e) {
          // Fallback to publicUsers collection (readable by all authenticated users)
          try {
            const publicUserDoc = await getDoc(doc(db, 'publicUsers', uid))
            if (publicUserDoc.exists()) {
              const userData = publicUserDoc.data()
              memberDetailsCache.value[uid] = userData
              details.push(normalizeMember(uid, userData))
            } else {
              details.push({ uid, displayName: 'Unknown User', email: '' })
            }
          } catch (e2) {
            details.push({ uid, displayName: 'Unknown User', email: '' })
          }
        }
      }
    }
    return details
  }

  // ═══════════════════════════════════════════════════════════════════
  // Messages Listener (for active group)
  // ═══════════════════════════════════════════════════════════════════

  async function openGroup(groupId) {
    // Stop previous listener
    stopMessagesListener()

    activeGroupId.value = groupId

    // Get group details
    const groupDoc = await getDoc(doc(db, 'groupChats', groupId))
    if (groupDoc.exists()) {
      const data = groupDoc.data()
      const memberDetails = await getMemberDetails(data.members || [])
      activeGroup.value = {
        groupId,
        ...data,
        memberDetails,
      }
    }

    // Start new listener
    const messagesRef = collection(db, 'groupChats', groupId, 'messages')

    // Fetch newest 100 first (matches userChatStore pattern). Threads with
    // >100 messages would otherwise be stuck at the oldest 100, hiding all
    // recent activity.
    const q = query(messagesRef, orderBy('createdAt', 'desc'), limit(100))

    messagesUnsubscribe.value = onSnapshot(
      q,
      snapshot => {
        messages.value = snapshot.docs
          .map(d => ({
            id: d.id,
            ...d.data(),
          }))
          .reverse()

        // Mark messages as read
        markMessagesAsRead(groupId)
      },
      error => {
        console.error('[GroupChatStore] Messages listener error:', error)
      }
    )

    // Start typing listener
    startTypingListener(groupId)
  }

  function closeGroup() {
    stopMessagesListener()
    stopTypingListener()
    activeGroupId.value = null
    activeGroup.value = null
    messages.value = []
  }

  function stopMessagesListener() {
    if (messagesUnsubscribe.value) {
      messagesUnsubscribe.value()
      messagesUnsubscribe.value = null
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // Create Group
  // ═══════════════════════════════════════════════════════════════════

  async function createGroup(name, memberUids) {
    if (!myUid.value || !name.trim()) return null

    // Ensure creator is in members
    const allMembers = [...new Set([myUid.value, ...memberUids])]

    try {
      // Create the group document
      const groupRef = await addDoc(collection(db, 'groupChats'), {
        name: name.trim(),
        createdBy: myUid.value,
        createdAt: serverTimestamp(),
        members: allMembers,
        lastMessage: `${myName.value} created this group`,
        lastMessageAt: serverTimestamp(),
        lastMessageBy: myUid.value,
      })

      // Initialize member status for all members
      const batch = writeBatch(db)
      for (const uid of allMembers) {
        const statusRef = doc(db, 'groupChats', groupRef.id, 'memberStatus', uid)
        batch.set(statusRef, {
          unreadCount: uid === myUid.value ? 0 : 1,
          mutedUntil: null,
          joinedAt: serverTimestamp(),
        })
      }

      // Add system message
      const messageRef = doc(collection(db, 'groupChats', groupRef.id, 'messages'))
      batch.set(messageRef, {
        type: 'system',
        text: `${myName.value} created this group`,
        createdAt: serverTimestamp(),
      })

      await batch.commit()

      console.log('[GroupChatStore] Created group:', groupRef.id)
      return groupRef.id
    } catch (error) {
      console.error('[GroupChatStore] Create group error:', error)
      throw error
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // Send Message (with optional attachment)
  // ═══════════════════════════════════════════════════════════════════

  async function sendMessage(text, attachment = null) {
    if ((!text || !text.trim()) && !attachment) return
    if (!activeGroupId.value) return

    const groupId = activeGroupId.value
    const trimmedText = text?.trim() || ''

    const messageData = {
      fromUid: myUid.value,
      fromName: myName.value,
      text: trimmedText,
      createdAt: serverTimestamp(),
      readBy: [myUid.value],
      reactions: {}, // Initialize empty reactions
    }

    // Add attachment if present
    if (attachment) {
      messageData.attachment = {
        url: attachment.url,
        name: attachment.name,
        type: attachment.type,
        size: attachment.size,
      }
    }

    const lastMessagePreview = attachment ? trimmedText || `📎 ${attachment.name}` : trimmedText

    try {
      const batch = writeBatch(db)

      // Add message
      const messageRef = doc(collection(db, 'groupChats', groupId, 'messages'))
      batch.set(messageRef, messageData)

      // Update group last message
      const groupRef = doc(db, 'groupChats', groupId)
      batch.update(groupRef, {
        lastMessage: lastMessagePreview,
        lastMessageAt: serverTimestamp(),
        lastMessageBy: myUid.value,
      })

      // Increment unread count for all other members (in the same batch for atomicity)
      const group = groups.value.find(g => g.groupId === groupId)
      if (group) {
        for (const memberUid of group.members) {
          if (memberUid !== myUid.value) {
            const statusRef = doc(db, 'groupChats', groupId, 'memberStatus', memberUid)
            batch.set(statusRef, { unreadCount: increment(1) }, { merge: true })
          }
        }
      }

      await batch.commit()

      // Clear typing indicator
      clearTypingIndicator()
    } catch (error) {
      console.error('[GroupChatStore] Send message error:', error)
      throw error
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // Delete Message
  // ═══════════════════════════════════════════════════════════════════

  async function deleteMessage(messageId) {
    if (!activeGroupId.value || !messageId) return

    try {
      const messageRef = doc(db, 'groupChats', activeGroupId.value, 'messages', messageId)
      await deleteDoc(messageRef)
      console.log('[GroupChatStore] Message deleted:', messageId)
    } catch (error) {
      console.error('[GroupChatStore] Delete message error:', error)
      throw error
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // Reactions
  // ═══════════════════════════════════════════════════════════════════

  async function addReaction(messageId, emoji) {
    if (!activeGroupId.value || !messageId) return

    try {
      const messageRef = doc(db, 'groupChats', activeGroupId.value, 'messages', messageId)

      await updateDoc(messageRef, {
        [`reactions.${emoji}`]: arrayUnion(myUid.value),
      })

      console.log('[GroupChatStore] Reaction added:', emoji)
    } catch (error) {
      console.error('[GroupChatStore] Add reaction error:', error)
      throw error
    }
  }

  async function removeReaction(messageId, emoji) {
    if (!activeGroupId.value || !messageId) return

    try {
      const messageRef = doc(db, 'groupChats', activeGroupId.value, 'messages', messageId)

      await updateDoc(messageRef, {
        [`reactions.${emoji}`]: arrayRemove(myUid.value),
      })

      console.log('[GroupChatStore] Reaction removed:', emoji)
    } catch (error) {
      console.error('[GroupChatStore] Remove reaction error:', error)
      throw error
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // Mark as Read
  // ═══════════════════════════════════════════════════════════════════

  async function markMessagesAsRead(groupId) {
    if (!myUid.value) return

    try {
      const statusRef = doc(db, 'groupChats', groupId, 'memberStatus', myUid.value)
      await setDoc(statusRef, { unreadCount: 0 }, { merge: true })
    } catch (error) {
      // Status doc might not exist yet, ignore
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // Typing Indicators
  // ═══════════════════════════════════════════════════════════════════

  let typingTimeout = null

  function startTypingListener(groupId) {
    const typingRef = collection(db, 'groupChats', groupId, 'typing')

    typingUnsubscribe.value = onSnapshot(typingRef, snapshot => {
      const groupTyping = {}
      snapshot.docs.forEach(d => {
        groupTyping[d.id] = d.data()
      })
      typingUsers.value = { ...typingUsers.value, [groupId]: groupTyping }
    })
  }

  function stopTypingListener() {
    if (typingUnsubscribe.value) {
      typingUnsubscribe.value()
      typingUnsubscribe.value = null
    }
  }

  async function setTypingIndicator() {
    if (!activeGroupId.value || !myUid.value) return

    // Clear any existing timeout
    if (typingTimeout) clearTimeout(typingTimeout)

    try {
      const typingRef = doc(db, 'groupChats', activeGroupId.value, 'typing', myUid.value)
      await setDoc(typingRef, {
        name: myName.value,
        timestamp: serverTimestamp(),
      })

      // Auto-clear after 3 seconds
      typingTimeout = setTimeout(() => {
        clearTypingIndicator()
      }, 3000)
    } catch (e) {
      // Ignore
    }
  }

  async function clearTypingIndicator() {
    if (!activeGroupId.value || !myUid.value) return

    if (typingTimeout) {
      clearTimeout(typingTimeout)
      typingTimeout = null
    }

    try {
      const typingRef = doc(db, 'groupChats', activeGroupId.value, 'typing', myUid.value)
      await deleteDoc(typingRef)
    } catch (e) {
      // Ignore
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // Group Management
  // ═══════════════════════════════════════════════════════════════════

  async function addMembers(groupId, newMemberUids) {
    if (!myUid.value || !groupId || !newMemberUids.length) return

    try {
      const batch = writeBatch(db)

      // Update group members array
      const groupRef = doc(db, 'groupChats', groupId)
      batch.update(groupRef, {
        members: arrayUnion(...newMemberUids),
      })

      // Initialize member status for new members
      for (const uid of newMemberUids) {
        const statusRef = doc(db, 'groupChats', groupId, 'memberStatus', uid)
        batch.set(statusRef, {
          unreadCount: 0,
          mutedUntil: null,
          joinedAt: serverTimestamp(),
        })
      }

      // Add system message
      const memberNames = await getMemberNames(newMemberUids)
      const messageRef = doc(collection(db, 'groupChats', groupId, 'messages'))
      batch.set(messageRef, {
        type: 'system',
        text: `${myName.value} added ${memberNames.join(', ')}`,
        createdAt: serverTimestamp(),
      })

      await batch.commit()

      // Refresh active group details
      if (activeGroupId.value === groupId) {
        await openGroup(groupId)
      }
    } catch (error) {
      console.error('[GroupChatStore] Add members error:', error)
      throw error
    }
  }

  async function removeMember(groupId, memberUid) {
    if (!myUid.value || !groupId || !memberUid) return

    try {
      const batch = writeBatch(db)

      // Update group members array
      const groupRef = doc(db, 'groupChats', groupId)
      batch.update(groupRef, {
        members: arrayRemove(memberUid),
      })

      // Add system message
      const memberNames = await getMemberNames([memberUid])
      const messageRef = doc(collection(db, 'groupChats', groupId, 'messages'))
      batch.set(messageRef, {
        type: 'system',
        text:
          memberUid === myUid.value
            ? `${myName.value} left the group`
            : `${myName.value} removed ${memberNames[0]}`,
        createdAt: serverTimestamp(),
      })

      await batch.commit()

      // If I left, close the group
      if (memberUid === myUid.value) {
        closeGroup()
      } else if (activeGroupId.value === groupId) {
        await openGroup(groupId)
      }
    } catch (error) {
      console.error('[GroupChatStore] Remove member error:', error)
      throw error
    }
  }

  async function updateGroupName(groupId, newName) {
    if (!myUid.value || !groupId || !newName.trim()) return

    try {
      const groupRef = doc(db, 'groupChats', groupId)
      await updateDoc(groupRef, { name: newName.trim() })

      // Add system message
      await addDoc(collection(db, 'groupChats', groupId, 'messages'), {
        type: 'system',
        text: `${myName.value} renamed the group to "${newName.trim()}"`,
        createdAt: serverTimestamp(),
      })

      // Update active group if viewing
      if (activeGroup.value && activeGroupId.value === groupId) {
        activeGroup.value.name = newName.trim()
      }
    } catch (error) {
      console.error('[GroupChatStore] Update group name error:', error)
      throw error
    }
  }

  async function muteGroup(groupId, duration) {
    if (!myUid.value || !groupId) return

    try {
      const statusRef = doc(db, 'groupChats', groupId, 'memberStatus', myUid.value)
      const mutedUntil = duration ? new Date(Date.now() + duration) : null
      await setDoc(statusRef, { mutedUntil }, { merge: true })
    } catch (error) {
      console.error('[GroupChatStore] Mute group error:', error)
      throw error
    }
  }

  function isGroupMuted(groupId) {
    const group = groups.value.find(g => g.groupId === groupId)
    if (!group?.mutedUntil) return false
    const mutedUntil = group.mutedUntil.toDate?.() || new Date(group.mutedUntil)
    return mutedUntil > new Date()
  }

  // ═══════════════════════════════════════════════════════════════════
  // Return
  // ═══════════════════════════════════════════════════════════════════

  return {
    // State
    myUid,
    myName,
    groups,
    activeGroupId,
    activeGroup,
    messages,
    totalUnreadCount,
    filteredGroups,
    isRecipientTyping,
    typingNames,
    searchQuery,
    sortBy,
    isGroupsTabActive,

    // Actions
    initialize,
    cleanup,
    openGroup,
    closeGroup,
    createGroup,
    sendMessage,
    deleteMessage,
    addReaction,
    removeReaction,
    setTypingIndicator,
    clearTypingIndicator,
    addMembers,
    removeMember,
    updateGroupName,
    muteGroup,
    isGroupMuted,
    getMemberDetails,
  }
})
